import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';

/** Lo que cambió en la foto: la nueva ya recortada, o null si la quitaron. */
export interface PatientPhotoChange
{
  blob: Blob | null;
}

/**
 * Foto del paciente con recorte cuadrado y zoom. La misma pieza sirve al dar de alta al
 * paciente y al editarlo, en la tienda y en el backoffice. No sube nada: entrega la foto
 * recortada y quien guarda el paciente decide cuándo subirla (así, si cancelan, no queda
 * un archivo huérfano en el almacenamiento).
 */
@Component({
  selector: 'app-patient-photo-picker',
  templateUrl: './patient-photo-picker.component.html',
  styleUrls: ['./patient-photo-picker.component.css']
})
export class PatientPhotoPickerComponent
{
  // Las iniciales que se muestran mientras no hay foto
  @Input() public name: string;
  @Input() public lastName: string;
  // La foto que ya tiene guardada, si tiene
  @Input() public photoUrl: string;

  @Output() public photoChange: EventEmitter<PatientPhotoChange> = new EventEmitter<PatientPhotoChange>();
  // Avisa mientras está recortando, para que no se pueda guardar a medias
  @Output() public croppingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('fileInput') private fileInput: ElementRef<HTMLInputElement>;

  // La foto sale de 512×512: se ve nítida en el detalle y pesa ~50 KB
  public readonly outputSize: number = 512;
  public readonly minZoom: number = 1;
  public readonly maxZoom: number = 3;
  private readonly maxFileSizeMb: number = 20;

  public sourceFile: File = null;
  public transform: ImageTransform = { scale: 1 };
  public previewUrl: string = null;
  public removed: boolean = false;
  public errorMessage: string = null;
  private lastCrop: string = null;

  get isCropping(): boolean
  {
    return this.sourceFile != null;
  }

  // Lo que se ve en el cuadro: la nueva si ya recortó, nada si la quitó, o la guardada
  get shownPhotoUrl(): string
  {
    if (this.previewUrl) { return this.previewUrl; }
    return this.removed ? null : this.photoUrl;
  }

  public openFilePicker(): void
  {
    this.fileInput.nativeElement.click();
  }

  public onFileSelected(event: Event): void
  {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Se limpia para que volver a elegir la misma foto también dispare el cambio
    input.value = '';

    if (!file) { return; }

    this.errorMessage = null;

    if (!file.type.startsWith('image/'))
    {
      this.errorMessage = 'Elige una imagen (JPG o PNG).';
      return;
    }

    if (file.size > this.maxFileSizeMb * 1024 * 1024)
    {
      this.errorMessage = `La foto pesa más de ${this.maxFileSizeMb} MB. Elige una más ligera.`;
      return;
    }

    this.transform = { scale: 1 };
    this.lastCrop = null;
    this.sourceFile = file;
    this.croppingChange.emit(true);
  }

  public onImageCropped(event: ImageCroppedEvent): void
  {
    this.lastCrop = event.base64;
  }

  public onLoadImageFailed(): void
  {
    this.errorMessage = 'No pudimos abrir esa foto. Intenta con una en JPG o PNG.';
    this.closeCropper();
  }

  public onZoomChange(value: number): void
  {
    this.transform = { ...this.transform, scale: value };
  }

  public zoomBy(step: number): void
  {
    const next = Math.min(this.maxZoom, Math.max(this.minZoom, (this.transform.scale ?? 1) + step));
    this.onZoomChange(next);
  }

  public confirmCrop(): void
  {
    if (!this.lastCrop) { return; }

    this.previewUrl = this.lastCrop;
    this.removed = false;
    this.photoChange.emit({ blob: this.base64ToBlob(this.lastCrop) });
    this.closeCropper();
  }

  public cancelCrop(): void
  {
    this.closeCropper();
  }

  public removePhoto(): void
  {
    this.previewUrl = null;
    this.removed = true;
    this.errorMessage = null;
    this.photoChange.emit({ blob: null });
  }

  private closeCropper(): void
  {
    this.sourceFile = null;
    this.lastCrop = null;
    this.croppingChange.emit(false);
  }

  private base64ToBlob(dataUrl: string): Blob
  {
    const [header, data] = dataUrl.split(',');
    const mime = header.match(/data:(.*?);/)?.[1] ?? 'image/jpeg';
    const bytes = atob(data);
    const buffer = new Uint8Array(bytes.length);

    for (let i = 0; i < bytes.length; i++)
    {
      buffer[i] = bytes.charCodeAt(i);
    }

    return new Blob([buffer], { type: mime });
  }
}
